import { ClipboardList, CreditCard, FileText, Plus, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom'

import {
  type GetMeResponse,
  type PublicMembershipDetails,
  type PublicPatient,
  possessive,
  REGION_IDS,
  TERMINATED_SUBSCRIPTION_STATUSES,
} from '@snootsvet/shared/portal'

import { ActionCard } from '@/customer/components/action-card'
import { PaymentMethods } from '@/customer/components/billing/payment-methods'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { MembershipWarnings } from '@/customer/components/membership-warnings'
import { PageContainer } from '@/customer/components/page-container'
import { BreedPetField } from '@/customer/components/pet/breed-pet-field'
import { DateOfBirthPetField } from '@/customer/components/pet/date-of-birth-pet-field'
import { MicrochipPetField } from '@/customer/components/pet/microchip-pet-field'
import { NamePetField } from '@/customer/components/pet/name-pet-field'
import { NeuterPetField } from '@/customer/components/pet/neuter-pet-field'
import { PetHeroCard } from '@/customer/components/pet/pet-hero-card'
import { PetSelect } from '@/customer/components/pet/pet-select'
import {
  AddPetTileContent,
  PetTileContent,
  petTileStyles,
} from '@/customer/components/pet/pet-tile'
import { SexPetField } from '@/customer/components/pet/sex-pet-field'
import { SpeciesPetField } from '@/customer/components/pet/species-pet-field'
import { Button } from '@/customer/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/customer/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/customer/components/ui/tabs'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { useIsImpersonated } from '@/customer/hooks/use-is-impersonated'
import { useIsMobile } from '@/customer/hooks/use-is-mobile'
import { usePetIssues } from '@/customer/hooks/use-pet-issues'
import { usePetWithSubscription } from '@/customer/hooks/use-pet-with-subscription'
import { useRegion } from '@/customer/hooks/use-region'
import { useUpdateSubscriptionPaymentMethod } from '@/customer/hooks/use-update-subscription-payment-method'
import { routes } from '@/customer/routes'

/**
 * Portal pets index.
 *
 * A tabbed page - one tab per pet, plus a trailing "Add a pet" pill
 * that navigates out to `/account/pets/new`. The selected tab shows
 * a pet and membership summary, membership warnings and actions,
 * and an editable details section.
 *
 * Deep links preserve the selected pet via `?pet=<id>`; older
 * `/account/pets/:patientId/edit` bookmarks are redirected here at
 * the router level.
 */
export function PetsPage() {
  useDocumentTitle('Your pets')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const me = useGetMe()
  const isMobile = useIsMobile()

  const headline = [{ text: 'Your pets', color: 'blue' as const }]

  if (me.isPending) {
    return (
      <PageContainer headline={headline}>
        <LoadingSpinner />
      </PageContainer>
    )
  }

  if (me.isError || !me.data) {
    return (
      <PageContainer headline={headline}>
        <p className="text-body-md text-destructive">
          Something went wrong loading your pets. Please try again in a moment.
        </p>
      </PageContainer>
    )
  }

  const { patients } = me.data

  if (patients.length === 0) {
    return (
      <PageContainer
        headline={[
          { text: 'Your', color: 'blue' },
          { text: '...pets?', color: 'pink' },
        ]}
        center
      >
        <div className="mt-6 flex flex-col gap-6">
          <p className="text-body-lg text-muted-foreground">It&apos;s a bit quiet in here...</p>
          <Button size="lg" className="h-14 px-8" onClick={() => navigate(routes.account.pets.add)}>
            <Plus />
            Add a pet
          </Button>
        </div>
      </PageContainer>
    )
  }

  // Hydrate the selected pet from `?pet=<id>` if present, otherwise
  // fall back to the first pet in the list. A stale / rogue value on
  // the URL falls through to the fallback too.
  const requestedId = searchParams.get('pet')
  const selectedId =
    (requestedId && patients.find((p) => p.id === requestedId)?.id) ?? patients[0].id

  const setSelectedId = (next: string) => {
    const params = new URLSearchParams(searchParams)
    params.set('pet', next)
    setSearchParams(params, { replace: true })
  }

  return (
    <PageContainer headline={headline}>
      <Tabs
        value={selectedId}
        onValueChange={(next) => {
          if (typeof next !== 'string') return
          setSelectedId(next)
        }}
        className="gap-6"
      >
        {isMobile === undefined ? null : isMobile ? (
          <div className="flex items-stretch gap-3">
            <PetSelect
              value={selectedId}
              onValueChange={setSelectedId}
              pets={patients}
              showBreed={false}
              className="flex-1"
            />
            <button
              type="button"
              aria-label="Add new pet"
              title="Add new pet"
              className={petTileStyles({ color: 'neutral' })}
              onClick={() => navigate(routes.account.pets.add)}
            >
              <AddPetTileContent monogramVariant="bare" />
            </button>
          </div>
        ) : (
          // `contents` flattens the TabsList's box out of the layout so
          // the triggers and the add-pet button share one flex-wrap
          // flow. `role="tablist"` is still honoured for a11y.
          <div className="flex flex-wrap items-stretch gap-3">
            <TabsList className="contents">
              {patients.map((pet) => (
                <TabsTrigger
                  key={pet.id}
                  value={pet.id}
                  className={petTileStyles({
                    color: pet.sex === 'male' ? 'baby-blue' : 'pink',
                  })}
                >
                  <PetTileContent
                    pet={pet}
                    isSelected={pet.id === selectedId}
                    monogramVariant="bare"
                    showBreed={false}
                  />
                </TabsTrigger>
              ))}
            </TabsList>
            <button
              type="button"
              aria-label="Add new pet"
              title="Add new pet"
              className={petTileStyles({ color: 'neutral' })}
              onClick={() => navigate(routes.account.pets.add)}
            >
              <AddPetTileContent monogramVariant="bare" />
            </button>
          </div>
        )}

        {patients.map((pet) => (
          <TabsContent key={pet.id} value={pet.id} className="flex flex-col gap-8 pt-2">
            <PetTabContent pet={pet} />
          </TabsContent>
        ))}
      </Tabs>
    </PageContainer>
  )
}

/**
 * Per-tab body. Fires its own `useGetPetSubscription` so the live
 * membership badge on the hero card streams in independently of the
 * other tabs. `hasClinicalActivity` comes from the `/me` snapshot so
 * the edit form's species/sex lock doesn't wait on the subscription
 * roundtrip.
 */
function PetTabContent({ pet }: { pet: GetMeResponse['patients'][number] }) {
  const petWithSub = usePetWithSubscription(pet.id)

  return (
    <>
      <PetHeroCard
        pet={pet}
        subscription={petWithSub.isPending ? undefined : petWithSub.membership?.subscription}
      />
      <PetMembershipActions pet={pet} membership={petWithSub.membership} />
      <EditPetSection pet={pet} hasClinicalActivity={pet.hasClinicalActivity} />
    </>
  )
}

function PetMembershipActions({
  pet,
  membership,
}: {
  pet: PublicPatient
  membership?: PublicMembershipDetails
}) {
  const navigate = useNavigate()
  const { isImpersonated } = useIsImpersonated()
  const { issue } = usePetIssues(pet.id)
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const update = useUpdateSubscriptionPaymentMethod(pet.id)
  const region = useRegion()
  const subscription = membership?.subscription
  const currentCard = subscription?.paymentMethod
  const canUpdate = subscription && !TERMINATED_SUBSCRIPTION_STATUSES.has(subscription.status)

  return (
    <div className="flex flex-col gap-4">
      {issue && <MembershipWarnings issues={[issue]} />}
      <div className="grid gap-3 sm:grid-cols-2">
        {canUpdate && (
          <ActionCard
            icon={CreditCard}
            label="Change payment method"
            tone="green"
            onClick={() => {
              setSelectedId(currentCard?.id ?? null)
              update.reset()
              setOpen(true)
            }}
          />
        )}
        {!!subscription?.availableUpgrades.length && (
          <ActionCard
            icon={TrendingUp}
            label="Upgrade membership"
            tone="yellow"
            onClick={() =>
              navigate(generatePath(routes.account.pets.upgrade, { patientId: pet.id }))
            }
          />
        )}
        {region.id === REGION_IDS.US && (
          <ActionCard
            icon={FileText}
            label="View records"
            tone="orange"
            onClick={() =>
              navigate(generatePath(routes.account.pets.records, { patientId: pet.id }))
            }
          />
        )}
        {isImpersonated && (
          <ActionCard
            icon={ClipboardList}
            label="Pre-visit intake"
            tone="baby-blue"
            onClick={() =>
              navigate(generatePath(routes.account.pets.intake, { patientId: pet.id }))
            }
          />
        )}
      </div>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!update.isPending) setOpen(next)
        }}
      >
        <DialogContent showCloseButton={!update.isPending}>
          <DialogHeader tone="green">
            <DialogTitle>Update your card</DialogTitle>
          </DialogHeader>
          {membership?.matchesMemberStripeCustomerId ? (
            <>
              <DialogBody>
                <DialogDescription>
                  Choose a default card for {possessive(pet.name)} future membership payments.
                </DialogDescription>
                <PaymentMethods value={selectedId} onChange={setSelectedId} />
                {update.isError && (
                  <p role="alert" className="text-label-sm text-destructive">
                    {update.error.message}
                  </p>
                )}
              </DialogBody>
              <DialogFooter>
                <Button
                  variant="outline"
                  disabled={update.isPending}
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!selectedId || selectedId === currentCard?.id || update.isPending}
                  onClick={() => {
                    if (selectedId)
                      update.mutate(
                        { paymentMethodId: selectedId },
                        { onSuccess: () => setOpen(false) }
                      )
                  }}
                >
                  {update.isPending ? 'Saving…' : 'Save card'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogBody>
                <DialogDescription>
                  Please contact support to update the card for this membership.
                </DialogDescription>
              </DialogBody>
              <DialogFooter>
                <Button onClick={() => setOpen(false)}>OK</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

/**
 * Field-by-field editable details for a single pet.
 *
 * Each row owns its own edit state, `useUpdatePet` mutation, and
 * error surface - the section itself has no shared draft or submit.
 *
 * Species and sex become read-only once the pet has clinical activity;
 * the affordance is hidden silently rather than shown-and-disabled.
 * The backend enforces the same restriction as defence-in-depth (see
 * `PATCH /api/portal/pets/:id`).
 *
 * Used inside the tabbed pets page - one section per selected pet.
 */
function EditPetSection({
  pet,
  hasClinicalActivity,
}: {
  pet: PublicPatient
  hasClinicalActivity: boolean
}) {
  return (
    <section className="flex flex-col">
      <h3 className="text-headline-sm">{possessive(pet.name)} Details</h3>
      <NamePetField pet={pet} />
      <SpeciesPetField pet={pet} hasClinicalActivity={hasClinicalActivity} />
      <BreedPetField pet={pet} />
      <DateOfBirthPetField pet={pet} />
      <SexPetField pet={pet} hasClinicalActivity={hasClinicalActivity} />
      <NeuterPetField pet={pet} />
      <MicrochipPetField pet={pet} />
    </section>
  )
}
